import type { Gender } from '@kimuno/api-sdk/lib/types/requests/User';
import React, { useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../actions/user.actions';
import { selectUser } from '../../reducers/user.selectors';
import { NotificationContext } from '../../state/NotificationContextProvider';
import useAction from '../../utils/useAction';
import EditableSection from '../EditableSection';
import BillingAddressForm, { type ChangeBillingAddressPayload } from './BillingAddressForm';
import BillingAddressView from './BillingAddressView';
import { getBillingAddressFields } from './billingAddressFields';
import { billingAddressSchema } from './billingAddressSchema';

const BillingAddressInfo = () => {
	const { t } = useTranslation();
	const updateMe = useAction(updateMeAction);
	const { showNotification } = useContext(NotificationContext);
	const user = useSelector(selectUser);
	const originalValuesRef = useRef<ChangeBillingAddressPayload | null>(null);
	const fields = getBillingAddressFields(user);

	const isBusinessUser = !!user?.isSchoolUser || !!user?.isLibraryUser || !!user?.isKeycloakSSO;
	const showInvoiceInformation = !user?.isReadOnlyLicence && !isBusinessUser;

	const { register, errors, control, getValues, triggerValidation, reset } =
		useForm<ChangeBillingAddressPayload>({
			mode: 'onBlur',
			validationSchema: billingAddressSchema,
			defaultValues: fields.reduce((acc, field) => {
				acc[field.name] = field.value || '';
				return acc;
			}, {} as ChangeBillingAddressPayload),
		});

	const onSaveChanges = async ({ toggleEditMode }: { toggleEditMode: () => void }) => {
		const isValid = await triggerValidation([
			'firstName',
			'lastName',
			'street',
			'postalCode',
			'city',
			'country',
			'institution',
		]);

		if (!isValid) return;

		if (!user) return;

		const formData = getValues();

		try {
			await updateMe({
				invoiceAddress: {
					firstName: formData.firstName,
					lastName: formData.lastName,
					street: formData.street,
					areaCode: formData.postalCode,
					city: formData.city,
					country: formData.country,
					institution: formData.institution || '',
					gender: user?.invoiceAddress.gender as Gender,
				},
			});
			toggleEditMode();
			showNotification({
				message: t('All changes were saved successfully'),
				severity: 'success',
			});
		} catch {
			showNotification({
				message: t('An error occurred while saving changes'),
				severity: 'error',
			});
		}
	};

	useEffect(() => {
		if (!user) return;

		const newFields = getBillingAddressFields(user);
		const newValues = newFields.reduce((acc, field) => {
			acc[field.name] = field.value || '';
			return acc;
		}, {} as ChangeBillingAddressPayload);

		originalValuesRef.current = newValues;
		reset(newValues);
	}, [user, reset]);

	if (!showInvoiceInformation) return null;

	return (
		<EditableSection
			title={t('Billing address')}
			onSaveChanges={onSaveChanges}
			dataTestId="billing-address-section"
		>
			{({ isEditMode }) =>
				isEditMode ? (
					<BillingAddressForm errors={errors} fields={fields} register={register} control={control} />
				) : (
					<BillingAddressView fields={fields} />
				)
			}
		</EditableSection>
	);
};

export default BillingAddressInfo;
